import { useState } from 'react';
import ApiClient from '@api/axiosSetup';
import { TObject, AJAXError } from '@types';
import { GenericAbortSignal } from 'axios';

type TSetupProps = {
	url: string;
	headers?: TObject,
	onUploading?: (progress: number) => void;
}

type TCallProps = {
	file: File;
	payloadFileKey?: string;
	signal?: GenericAbortSignal;
	headers?: { [key: string]: string }
	domain?: 'EOS' | 'ShipmentAPI' | 'AdminAPI' | 'Inertia'
	additionalFields?: Record<string, string>;
}

type InternalState = {
	progress: null | number;
	uploadComplete: boolean;
	inProgress: boolean;
}

export const useFileUpload = (props: TSetupProps) => {
	const {
		url,
		headers,
		onUploading
	} = props;
	const [uploadData, setUploadData] = useState<any>(null);
	const [internalState, setInternalState] = useState<InternalState>({
		progress: null,
		uploadComplete: false,
		inProgress: false
	})
	const fullHeaders = { ...headers }
	if (!fullHeaders['Accept']) {
		fullHeaders['Accept'] = 'application/json';
	}
	if (!fullHeaders['Content-Type']) {
		fullHeaders['Content-Type'] = 'multipart/form-data';
	}
	const uploadFile = async (callProps: TCallProps) => {
		await setInternalState({
			progress: null,
			uploadComplete: false,
			inProgress: true
		});
		try {
			const formData = new FormData();
			formData.append(callProps.payloadFileKey ?? 'document', callProps.file);
			if (callProps.additionalFields) {
				Object.entries(callProps.additionalFields).forEach(([key, value]) => formData.append(key, value));
			}
			let theURL = url
			if (callProps.domain && callProps.domain !== 'EOS') {
				theURL = `/${callProps.domain?.toLowerCase()}${theURL}`;
			}
			const callAPI = await ApiClient.request({
				url: theURL,
				method: 'POST',
				data: formData,
				headers: { ...fullHeaders, ...callProps.headers },
				signal: callProps.signal,
				onUploadProgress: (progressEvent) => {
					const uploadProgress = (progressEvent.loaded / (progressEvent.total ?? 0)) * 100;
					setInternalState({
						...internalState,
						progress: uploadProgress,
					});
					if (onUploading) onUploading(uploadProgress);
				}
			});
			setUploadData(callAPI);
			setInternalState({
				...internalState,
				progress: null,
				uploadComplete: true,
				inProgress: false
			});
			return {
				data: callAPI.data,
				error: null
			}
		} catch (e) {
			if ((e as AJAXError).code && (e as AJAXError).code === 'ERR_CANCELED') return;
			setInternalState({
				...internalState,
				progress: null,
				uploadComplete: true,
				inProgress: false
			});
			throw new Error((e as AJAXError).response.data.message, { cause: e });
		}
	}

	return {
		uploadFile,
		progress: internalState.progress,
		uploadComplete: internalState.uploadComplete,
		inProgress: internalState.inProgress,
		uploadData
	} as const
}
